import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import PhoneMockup from "@/components/PhoneMockup";
import EventGalleryScreen from "@/components/mockups/EventGalleryScreen";
import CameraScreen from "@/components/mockups/CameraScreen";

// Bali/Indonesia-themed, face-less imagery for phone mockups
const GALLERY_IMG =
  "https://images.unsplash.com/photo-1537996194471-e657df975ab4?crop=entropy&cs=srgb&fm=jpg&w=940&q=85";
const CAMERA_IMG =
  "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&w=940&q=85";

export default function SplitFeatures() {
  const { t } = useLang();
  return (
    <>
      {/* 02. Create Your Event */}
      <section
        id="create-event"
        data-testid="create-event-section"
        className="relative bg-white"
      >
        <div className="absolute inset-y-0 left-0 hidden w-1/2 bg-[#DDE7E4] lg:block" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 py-24 lg:grid-cols-2 lg:gap-24 lg:px-8 lg:py-32">
          <div className="flex justify-center bg-[#DDE7E4] py-12 lg:bg-transparent lg:py-0">
            <PhoneMockup data-testid="create-event-phone">
              <EventGalleryScreen
                title="Sarah & David"
                date="12.06.2026"
                photos={342}
                videos={58}
                contributors={87}
                image={GALLERY_IMG}
              />
            </PhoneMockup>
          </div>
          <FeatureText
            number={t.create.number}
            title={t.create.title}
            body={t.create.body}
            cta={t.create.cta}
            testId="create"
          />
        </div>
      </section>

      {/* 03. Collect Every Moment */}
      <section
        id="collect-moment"
        data-testid="collect-section"
        className="relative bg-[#DDE7E4]"
      >
        <div className="absolute inset-y-0 right-0 hidden w-1/2 bg-white lg:block" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 py-24 lg:grid-cols-2 lg:gap-24 lg:px-8 lg:py-32">
          <FeatureText
            number={t.collect.number}
            title={t.collect.title}
            body={t.collect.body}
            cta={t.collect.cta}
            testId="collect"
            className="lg:order-2"
          />
          <div className="flex justify-center lg:order-1">
            <PhoneMockup data-testid="collect-phone">
              <CameraScreen image={CAMERA_IMG} />
            </PhoneMockup>
          </div>
        </div>
      </section>
    </>
  );
}

function FeatureText({ number, title, body, cta, testId, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      className={`max-w-lg ${className}`}
    >
      <span className="font-['Playfair_Display'] text-lg text-neutral-500">{number}</span>
      <h3 className="mt-2 font-['Outfit'] text-4xl font-light tracking-tight text-neutral-950 sm:text-5xl">
        {title}
      </h3>
      <p className="mt-6 text-base leading-relaxed text-neutral-600 lg:text-lg">{body}</p>
      <a
        href="#reach"
        data-testid={`feature-cta-${testId}`}
        className="group mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-neutral-950"
      >
        {cta}
        <ArrowRight
          className="h-4 w-4 transition-transform group-hover:translate-x-1"
          strokeWidth={1.5}
        />
      </a>
    </motion.div>
  );
}
